import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import { DownloadOutlined, InboxOutlined, UploadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import styled from "styled-components";
import * as XLSX from "xlsx";
import axios from "axios";
import { Container } from "../ViewGerenciarPostagens/styles";
import { useMessage } from "../../uteis/mensagens/useMessage";
import { buscarEtiquetasService } from "../../services/buscarEtiquetasService";
import { importarLeadsPlanilhaService } from "../../services/importarLeadsPlanilhaService";
import {
  ImportacaoLeadErro,
  ImportacaoLeadResponse,
} from "../../interfaces/ImportacaoLeads";

type EtiquetaOption = {
  id: number;
  nome: string;
  cor?: string | null;
  active?: number;
};

const SectionCard = styled(Card)`
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 54, 68, 0.08);
`;

const InstructionList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #3f4b53;
  line-height: 1.7;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
  margin-top: 20px;
`;

const StatCard = styled.div`
  padding: 16px;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f5f9fa 100%);
  border: 1px solid #d6e6ea;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #5f6d75;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #003644;
`;

const ResultPlaceholder = styled.div`
  padding: 32px 20px;
  text-align: center;
  color: #5f6d75;
`;

const DraggerWrapper = styled.div`
  .ant-upload-wrapper {
    width: 100%;
  }
`;

const resumoLabels: Record<string, string> = {
  totalLinhasLidas: "Linhas lidas",
  linhasProcessadas: "Linhas processadas",
  linhasIgnoradas: "Linhas ignoradas",
  pessoasCriadas: "Pessoas criadas",
  leadsCriados: "Leads criados",
  etiquetasVinculadas: "Etiquetas vinculadas",
};

const erroColumns: ColumnsType<ImportacaoLeadErro> = [
  {
    title: "Linha",
    dataIndex: "linha",
    key: "linha",
    width: 90,
  },
  {
    title: "Telefone",
    dataIndex: "telefone",
    key: "telefone",
    render: (value: string) => value || "-",
  },
  {
    title: "Nome",
    dataIndex: "nome",
    key: "nome",
    render: (value: string) => value || "-",
  },
  {
    title: "Motivo",
    dataIndex: "motivo",
    key: "motivo",
  },
];

const obterMensagemErroImportacao = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const backendMessage = data?.message;
    const backendError = data?.error;

    if (typeof backendMessage === "string" && backendMessage.trim()) {
      return backendMessage;
    }

    if (Array.isArray(backendMessage) && backendMessage.length > 0) {
      return backendMessage.join(" ");
    }

    if (typeof backendError === "string" && backendError.trim()) {
      return backendError;
    }

    if (status === 400) {
      return "Não foi possível importar a planilha. Verifique o arquivo e as etiquetas selecionadas.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível importar a planilha. Tente novamente.";
};

export default function ViewImportarLeadsPlanilha() {
  const { Title, Paragraph, Text } = Typography;
  const isMobile = useMediaQuery("(max-width:600px)");
  const { showError, showSuccess, showWarning, contextHolder } = useMessage();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  const [etiquetasOptions, setEtiquetasOptions] = useState<EtiquetaOption[]>([]);
  const [etiquetasSelecionadas, setEtiquetasSelecionadas] = useState<number[]>([]);
  const [carregandoEtiquetas, setCarregandoEtiquetas] = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState<ImportacaoLeadResponse | null>(null);

  useEffect(() => {
    const carregarEtiquetas = async () => {
      setCarregandoEtiquetas(true);

      try {
        const data = await buscarEtiquetasService();

        if (Array.isArray(data)) {
          const etiquetasAtivas = data
            .filter((etiqueta) => etiqueta.active === 1)
            .map((etiqueta) => ({
              id: etiqueta.id,
              nome: etiqueta.nome,
              cor: etiqueta.cor ?? null,
              active: etiqueta.active,
            }))
            .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

          setEtiquetasOptions(etiquetasAtivas);
        }
      } catch (error) {
        showError("Não foi possível carregar as etiquetas.");
      } finally {
        setCarregandoEtiquetas(false);
      }
    };

    carregarEtiquetas();
  }, [showError]);

  const estatisticasResumo = useMemo(
    () =>
      resultado
        ? [
            { key: "totalLinhasLidas", value: resultado.totalLinhasLidas },
            { key: "linhasProcessadas", value: resultado.linhasProcessadas },
            { key: "linhasIgnoradas", value: resultado.linhasIgnoradas },
            { key: "pessoasCriadas", value: resultado.pessoasCriadas },
            { key: "leadsCriados", value: resultado.leadsCriados },
            { key: "etiquetasVinculadas", value: resultado.etiquetasVinculadas },
          ]
        : [],
    [resultado]
  );

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    const extensao = file.name.split(".").pop()?.toLowerCase();

    if (!extensao || !["xls", "xlsx"].includes(extensao)) {
      showError("Selecione um arquivo Excel válido (.xls ou .xlsx).");
      return Upload.LIST_IGNORE;
    }

    setArquivoSelecionado(file as File);
    setFileList([
      {
        uid: file.uid,
        name: file.name,
        size: file.size,
        type: file.type,
        originFileObj: file,
        status: "done",
      },
    ]);
    setResultado(null);
    return false;
  };

  const handleRemove = () => {
    setArquivoSelecionado(null);
    setFileList([]);
    setResultado(null);
    return true;
  };

  const handleBaixarModelo = () => {
    try {
      const worksheet = XLSX.utils.aoa_to_sheet([
        ["telefone", "nome"],
        ["553498287879", "Maria Exemplo"],
      ]);
      const workbook = XLSX.utils.book_new();

      // Reforça a primeira coluna como texto para reduzir conversões automáticas do Excel.
      worksheet["A1"].t = "s";
      worksheet["A2"].t = "s";
      worksheet["A2"].z = "@";
      worksheet["!cols"] = [{ wch: 22 }, { wch: 30 }];

      XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
      XLSX.writeFile(workbook, "modelo-importacao-leads.xlsx");
      showSuccess("Modelo baixado com sucesso!");
    } catch (error) {
      showError("Não foi possível gerar o modelo da planilha.");
    }
  };

  const handleImportar = async () => {
    if (!(arquivoSelecionado instanceof File)) {
      showError("Selecione um arquivo para importar.");
      return;
    }

    setImportando(true);
    setResultado(null);

    try {
      const response = await importarLeadsPlanilhaService(
        arquivoSelecionado,
        etiquetasSelecionadas
      );

      setResultado(response);

      if (response.status === "ERRO" || response.erros.length > 0) {
        showWarning("Importação concluída com ressalvas. Confira o resumo e as linhas com erro.");
        return;
      }

      showSuccess("Importação concluída com sucesso!");
    } catch (error) {
      showError(obterMensagemErroImportacao(error));
    } finally {
      setImportando(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Container>
        <Row>
          <Col span={5}>
            <Flex align="center" justify="center" style={{ marginTop: 15 }}>
              <div aria-hidden="true" />
            </Flex>
          </Col>
          <Col span={12}>
            <Flex align="center" justify="center">
              <HomeIcon color="primary" fontSize="large" />
              <Title
                style={{
                  marginLeft: 10,
                  marginTop: 10,
                  fontSize: isMobile ? "1rem" : "2rem",
                  color: "rgb(0, 54, 68)",
                }}
                level={3}
              >
                Importar Leads por Planilha
              </Title>
            </Flex>
          </Col>
        </Row>

        <Row>
          <Col span={isMobile ? 24 : 2}></Col>
          <Col
            span={isMobile ? 24 : 20}
            style={{ marginTop: 16, marginBottom: 40 }}
          >
            <Space direction="vertical" size={20} style={{ width: "100%" }}>
              <SectionCard>
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} md={15}>
                    <Title level={4} style={{ color: "#003644", marginTop: 0 }}>
                      Como funciona a importação
                    </Title>
                    <InstructionList>
                      <li>Envie um arquivo `.xls` ou `.xlsx` com 2 colunas: telefone e nome.</li>
                      <li>No Excel, mantenha a coluna de telefone formatada como Texto para evitar notação científica.</li>
                      <li>A primeira linha deve ser o cabeçalho e será ignorada pelo backend.</li>
                      <li>As etiquetas são opcionais e serão aplicadas às pessoas importadas.</li>
                      <li>O backend reutiliza Pessoa e Lead existentes e evita duplicar etiquetas.</li>
                    </InstructionList>
                  </Col>
                  <Col xs={24} md={9}>
                    <Space
                      direction={isMobile ? "vertical" : "horizontal"}
                      style={{ width: "100%", justifyContent: "flex-end" }}
                    >
                      <Button icon={<DownloadOutlined />} onClick={handleBaixarModelo}>
                        Baixar modelo
                      </Button>
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={handleImportar}
                        loading={importando}
                        style={{
                          backgroundColor: "#003644",
                          borderColor: "#003644",
                        }}
                      >
                        Importar planilha
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </SectionCard>

              <SectionCard>
                <Row gutter={[20, 20]}>
                  <Col xs={24} lg={14}>
                    <Title level={5} style={{ color: "#003644", marginTop: 0 }}>
                      Arquivo
                    </Title>
                    <DraggerWrapper>
                      <Upload.Dragger
                        accept=".xls,.xlsx"
                        beforeUpload={beforeUpload}
                        fileList={fileList}
                        maxCount={1}
                        onRemove={handleRemove}
                        multiple={false}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined style={{ color: "#003644" }} />
                        </p>
                        <p className="ant-upload-text">
                          Clique ou arraste a planilha para esta área
                        </p>
                        <p className="ant-upload-hint">
                          Apenas arquivos Excel com telefone na primeira coluna e nome na segunda.
                        </p>
                      </Upload.Dragger>
                    </DraggerWrapper>
                  </Col>

                  <Col xs={24} lg={10}>
                    <Title level={5} style={{ color: "#003644", marginTop: 0 }}>
                      Etiquetas
                    </Title>
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      loading={carregandoEtiquetas}
                      placeholder="Selecione etiquetas (opcional)"
                      style={{ width: "100%" }}
                      optionFilterProp="label"
                      value={etiquetasSelecionadas}
                      onChange={(value) => setEtiquetasSelecionadas(value)}
                      options={etiquetasOptions.map((etiqueta) => ({
                        value: etiqueta.id,
                        label: etiqueta.nome,
                      }))}
                    />

                    <Paragraph style={{ marginTop: 16, marginBottom: 0, color: "#5f6d75" }}>
                      As etiquetas selecionadas serão enviadas no campo `etiquetaIds`.
                    </Paragraph>
                    <Paragraph style={{ marginTop: 12, marginBottom: 0, color: "#5f6d75" }}>
                      Dica: use o modelo baixado nesta tela e preencha a coluna `telefone` sem alterar o formato de texto.
                    </Paragraph>
                  </Col>
                </Row>
              </SectionCard>

              <SectionCard>
                <Flex
                  justify="space-between"
                  align={isMobile ? "flex-start" : "center"}
                  vertical={isMobile}
                  gap={12}
                >
                  <div>
                    <Title level={4} style={{ color: "#003644", margin: 0 }}>
                      Resultado da importação
                    </Title>
                    <Paragraph style={{ margin: "8px 0 0", color: "#5f6d75" }}>
                      O resumo aparece aqui assim que a planilha for processada.
                    </Paragraph>
                  </div>

                  {resultado && (
                    <Space wrap>
                      <Tag color={resultado.status === "SUCESSO" ? "success" : "warning"}>
                        {resultado.status}
                      </Tag>
                      <Text strong>{resultado.arquivo}</Text>
                    </Space>
                  )}
                </Flex>

                {!resultado ? (
                  <ResultPlaceholder>
                    Nenhuma importação foi executada ainda.
                  </ResultPlaceholder>
                ) : (
                  <>
                    <Alert
                      style={{ marginTop: 20 }}
                      type={resultado.status === "SUCESSO" ? "success" : "warning"}
                      message={
                        resultado.status === "SUCESSO"
                          ? "Importação concluída com sucesso."
                          : "Importação concluída com processamento parcial."
                      }
                      description={
                        resultado.erros.length > 0
                          ? `Foram encontradas ${resultado.erros.length} linha(s) com problema.`
                          : "Nenhuma linha com erro foi retornada pelo backend."
                      }
                      showIcon
                    />

                    <StatsGrid>
                      {estatisticasResumo.map((item) => (
                        <StatCard key={item.key}>
                          <StatLabel>{resumoLabels[item.key]}</StatLabel>
                          <StatValue>{item.value}</StatValue>
                        </StatCard>
                      ))}
                    </StatsGrid>

                    <Space wrap style={{ marginTop: 20 }}>
                      <Text strong>Etiquetas enviadas:</Text>
                      {resultado.etiquetaIds.length > 0 ? (
                        resultado.etiquetaIds.map((id) => (
                          <Tag color="blue" key={id}>
                            #{id}
                          </Tag>
                        ))
                      ) : (
                        <Text type="secondary">Nenhuma etiqueta foi enviada.</Text>
                      )}
                    </Space>

                    {resultado.erros.length > 0 && (
                      <div style={{ marginTop: 24 }}>
                        <Title level={5} style={{ color: "#003644" }}>
                          Linhas com problema
                        </Title>
                        <Table
                          rowKey={(record, index) => `${record.linha}-${index}`}
                          columns={erroColumns}
                          dataSource={resultado.erros}
                          bordered
                          pagination={{ pageSize: 5 }}
                          scroll={{ x: 720 }}
                        />
                      </div>
                    )}
                  </>
                )}
              </SectionCard>
            </Space>
          </Col>
          <Col span={isMobile ? 24 : 2}></Col>
        </Row>
      </Container>
    </>
  );
}
